import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  PictureAsPdf as PdfIcon,
  TableView as ExcelIcon,
} from '@mui/icons-material';
import api from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { PDFDocument } from 'pdf-lib';
import DateField from './DateField';
import { formatDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface MedicalEvent {
  cat_id: number;
  chip_id: string | null;
  event_type: string | null;
  event_date: string | null;
  cost: number | null;
  veterinary_name: string | null;
  notes: string | null;
  invoice_file_path: string | null;
}

interface ReportData {
  cat_id: number;
  cat_name: string;
  chip_id: string | null;
  sex: string | null;
  breed: string | null;
  municipality: string | null;
  address_where_found: string | null;
  status: string;
  status_date: string | null;
  external?: boolean;
  total_medical_expenses: number;
  daily_allowance: number;
  days_in_period: number;
  total_daily_allowance: number;
  total_expenses: number;
  cost_class: string | null;
  cost_class_multiplier: number | null;
  effective_daily_rate: number | null;
  cost_class_periods: string | null;
}

// Helper functions to get current month's start and end dates
const getFirstDayOfMonth = (): string => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  return firstDay.toISOString().split('T')[0];
};

const getLastDayOfMonth = (): string => {
  const today = new Date();
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return lastDay.toISOString().split('T')[0];
};

const Report: React.FC = () => {
  const { t } = useTranslation();

  const translateStatus = (status: string, external?: boolean): string => {
    // External (non-shelter) cats are labelled as such, not by shelter status.
    if (external) return t('report.external');
    // The backend returns lowercase status values: active/adopted/freed/angel.
    const map: Record<string, string> = {
      'active': t('catStatus.active'),
      'adopted': t('catStatus.adopted'),
      'freed': t('catStatus.freed'),
      'angel': t('catStatus.deceased'),
    };
    return map[status?.toLowerCase()] || status;
  };

  const translateCostClass = (cc: string | null): string => {
    const map: Record<string, string> = {
      'Ordinary': t('cat.costClasses.ordinary'),
      'Increased': t('cat.costClasses.increased'),
      'Increased Kitten': t('cat.costClasses.increasedKitten'),
      'Increased Sickroom': t('cat.costClasses.increasedSickroom'),
      'Other': t('cat.costClasses.other'),
    };
    return cc ? (map[cc] || cc) : t('cat.costClasses.ordinary');
  };

  const [startDate, setStartDate] = useState<string>(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState<string>(getLastDayOfMonth());
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>('');
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [medicalEvents, setMedicalEvents] = useState<MedicalEvent[]>([]);
  const [municipalityExpenses, setMunicipalityExpenses] = useState<any[]>([]);
  const [recoveryAllowances, setRecoveryAllowances] = useState<any[]>([]);
  // Sterilizations carried by cumulative invoices: kept out of the itemized money
  // detail, but counted in the summary recap so it matches the statistics page.
  const [invoiceSterilizations, setInvoiceSterilizations] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load municipalities on mount
  useEffect(() => {
    loadMunicipalities();
  }, []);

  const loadMunicipalities = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/municipalities/data?limit=1000`);
      const municipalitiesData = response.data.rows.map((row: any[]) => row[0]);
      setMunicipalities(municipalitiesData.sort());
    } catch (error) {
      console.error('Error loading municipalities:', error);
      setMessage({ type: 'error', text: t('report.failedToLoadMunicipalities') });
    }
  };

  const handleGenerateReport = async () => {
    // Validate inputs
    if (!startDate || !endDate || !selectedMunicipality) {
      setMessage({ type: 'error', text: t('report.fillAllFields') });
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setMessage({ type: 'error', text: t('report.startDateBeforeEndDate') });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await api.post(`${API_URL}/api/report/expenses`, {
        start_date: startDate,
        end_date: endDate,
        municipality: selectedMunicipality,
      });

      const cats: ReportData[] = response.data.cats || [];
      const events = response.data.medical_events || [];
      setReportData(cats);
      setMedicalEvents(events);
      const munExp = response.data.municipality_expenses || [];
      setMunicipalityExpenses(munExp);
      const recExp = response.data.recovery_allowances || [];
      setRecoveryAllowances(recExp);
      setInvoiceSterilizations(response.data.invoice_sterilizations || []);

      if (cats.length === 0 && munExp.length === 0 && recExp.length === 0) {
        setMessage({ type: 'success', text: t('report.noDataFound') });
      } else {
        const totalMedical = cats.reduce((sum: number, cat: ReportData) => sum + cat.total_medical_expenses, 0);
        const totalAllowance = cats.reduce((sum: number, cat: ReportData) => sum + cat.total_daily_allowance, 0);
        const grandTotal = cats.reduce((sum: number, cat: ReportData) => sum + cat.total_expenses, 0);
        setMessage({
          type: 'success',
          text: t('report.reportGeneratedSummary', {
            catCount: cats.length,
            medical: totalMedical.toFixed(2),
            allowance: totalAllowance.toFixed(2),
            total: grandTotal.toFixed(2)
          })
        });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: t('report.failedToGenerateReport', { error: error.response?.data?.detail || error.message })
      });
      setReportData([]);
      setMedicalEvents([]);
      setMunicipalityExpenses([]);
      setRecoveryAllowances([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalMedicalExpenses = (data: ReportData[]): number => {
    return data.reduce((sum, cat) => sum + cat.total_medical_expenses, 0);
  };

  const calculateTotalDailyAllowance = (data: ReportData[]): number => {
    return data.reduce((sum, cat) => sum + cat.total_daily_allowance, 0);
  };

  const municipalExpensesTotal = (): number =>
    municipalityExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  const recoveryTotal = (): number =>
    recoveryAllowances.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  // Split the report data into shelter cats and externally-managed cats.
  const shelterCats = (): ReportData[] => reportData.filter((c) => !c.external);
  const externalCats = (): ReportData[] => reportData.filter((c) => !!c.external);
  const shelterMedicalEvents = (): MedicalEvent[] => {
    const ids = new Set(shelterCats().map((c) => c.cat_id));
    return medicalEvents.filter((ev) => ids.has(ev.cat_id));
  };
  const externalMedicalEvents = (): MedicalEvent[] => {
    const ids = new Set(externalCats().map((c) => c.cat_id));
    return medicalEvents.filter((ev) => ids.has(ev.cat_id));
  };
  const sumTotalExpenses = (arr: ReportData[]): number => arr.reduce((s, c) => s + c.total_expenses, 0);
  const shelterTotal = (): number => sumTotalExpenses(shelterCats());
  const externalTotal = (): number => sumTotalExpenses(externalCats());
  const overallTotal = (): number => shelterTotal() + externalTotal() + municipalExpensesTotal() + recoveryTotal();

  // Strip characters that jsPDF's built-in Helvetica cannot render
  const pdf = (s: string) => s.replace(/€/g, 'EUR').replace(/→/g, '->').replace(/×/g, 'x');
  // Format ISO date string YYYY-MM-DD -> DD/MM/YYYY for PDF output
  const dmyPdf = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  type CostPeriod = { cost_class: string; from: string; until: string; days: number; daily_rate: number; amount: number };

  const formatCostClassCell = (costClassJson: string | null, separator: string, dmyFn: (s: string) => string): string => {
    const periods: CostPeriod[] = JSON.parse(costClassJson || '[]');
    if (!periods.length) return t('cat.costClasses.ordinary');
    return periods.map((p) =>
      `${translateCostClass(p.cost_class)}  |  ${dmyFn(p.from)} – ${dmyFn(p.until)}  |  ${p.days}${t('report.daysAbbr')} × €${p.daily_rate.toFixed(2)} = €${p.amount.toFixed(2)}`
    ).join(separator);
  };

  const formatCostClassCellPdf = (costClassJson: string | null): string => {
    const periods: CostPeriod[] = JSON.parse(costClassJson || '[]');
    if (!periods.length) return pdf(t('cat.costClasses.ordinary'));
    return periods.map((p) =>
      `${pdf(translateCostClass(p.cost_class))}  |  ${dmyPdf(p.from)} - ${dmyPdf(p.until)}  |  ${p.days}${t('report.daysAbbr')} x EUR ${p.daily_rate.toFixed(2)} = EUR ${p.amount.toFixed(2)}`
    ).join('\n');
  };

  const translateEventType = (et: string | null): string => {
    if (!et) return t('common.na');
    const map: Record<string, string> = {
      'Vaccine': t('medical.eventTypes.vaccine'),
      'Sterilization': t('medical.eventTypes.sterilization'),
      'Treatment': t('medical.eventTypes.treatment'),
      'Delivery': t('medical.eventTypes.delivery'),
      'Surgery': t('medical.eventTypes.surgery'),
      'Checkup': t('medical.eventTypes.checkup'),
      'test_fiv_felv': t('medical.eventTypes.testFivFelv'),
      'Travel Expenses': t('medical.eventTypes.travelExpenses'),
      'Other': t('medical.eventTypes.other'),
    };
    return map[et] || et;
  };

  const handleExportPDF = async () => {
    const doc = new jsPDF({ orientation: 'landscape' });

    // Add title
    doc.setFontSize(18);
    doc.text(pdf(t('report.title')), 14, 20);

    // Add report details
    doc.setFontSize(11);
    doc.text(`${t('cat.municipality')}: ${selectedMunicipality === 'All' ? t('report.allMunicipalities') : selectedMunicipality}`, 14, 30);
    doc.text(`${t('report.dateRange')} ${dmyPdf(startDate)} - ${dmyPdf(endDate)}`, 14, 37);
    doc.text(`${t('report.totalCats')} ${reportData.length}`, 14, 44);

    // Prepare table data for shelter cats
    const shelterList = shelterCats();
    const tableData: any[] = shelterList.map((cat) => [
      cat.cat_id.toString(),
      cat.chip_id?.toString() || t('cat.missing'),
      cat.cat_name || t('cat.unnamed'),
      cat.status_date
        ? `${pdf(translateStatus(cat.status, cat.external))}\n${dmyPdf(cat.status_date.toString().slice(0, 10))}`
        : pdf(translateStatus(cat.status, cat.external)),
      cat.sex || t('common.na'),
      cat.address_where_found || t('common.na'),
      formatCostClassCellPdf(cat.cost_class_periods),
      `EUR ${cat.total_medical_expenses.toFixed(2)}`,
      `EUR ${cat.total_daily_allowance.toFixed(2)}`,
      `EUR ${cat.total_expenses.toFixed(2)}`,
    ]);

    // Add cat totals row
    tableData.push([
      { content: pdf(t('report.catExpensesSubtotal')), colSpan: 7, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: `EUR ${calculateTotalMedicalExpenses(shelterList).toFixed(2)}`, styles: { fontStyle: 'bold' } },
      { content: `EUR ${calculateTotalDailyAllowance(shelterList).toFixed(2)}`, styles: { fontStyle: 'bold' } },
      { content: `EUR ${shelterTotal().toFixed(2)}`, styles: { fontStyle: 'bold' } },
    ]);

    // Shelter cats table heading
    doc.setFontSize(13);
    doc.text(pdf(t('report.shelterCatExpenses')), 14, 50);

    // Generate cats table
    autoTable(doc, {
      head: [[
        pdf(t('report.catId')),
        pdf(t('report.chipId')),
        pdf(t('report.name')),
        pdf(t('report.status')),
        pdf(t('report.sex')),
        pdf(t('report.addressWhereFound')),
        pdf(t('report.costClass')),
        pdf(t('report.totalMedicalExpenses')),
        pdf(t('report.totalDailyAllowance')),
        pdf(t('report.totalExpenses')),
      ]],
      body: tableData,
      startY: 55,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [237, 108, 2] },
      columnStyles: {
        7: { halign: 'right' },
        8: { halign: 'right' },
        9: { halign: 'right' },
      },
    });

    // Shelter cats medical events table (subtotals summary + grand total come after all tables)
    if (shelterMedicalEvents().length > 0) {
      const pageHeight = doc.internal.pageSize.getHeight();
      const prevY = (doc as any).lastAutoTable?.finalY || 55;
      let medStartY: number;
      if (prevY + 30 > pageHeight - 20) { doc.addPage(); medStartY = 20; } else { medStartY = prevY + 15; }
      doc.setFontSize(13);
      doc.text(pdf(t('report.shelterMedicalEventsTitle')), 14, medStartY);

      const shelterEvents = shelterMedicalEvents();
      const medTableData = shelterEvents.map((ev) => [
        ev.cat_id.toString(),
        ev.chip_id || t('cat.missing'),
        ev.event_date ? dmyPdf(ev.event_date) : t('common.na'),
        pdf(translateEventType(ev.event_type)),
        ev.cost != null ? `EUR ${Number(ev.cost).toFixed(2)}` : t('common.na'),
        ev.veterinary_name || t('common.na'),
        ev.notes || '',
      ]);

      const medTotalCost = shelterEvents.reduce((s, ev) => s + (ev.cost || 0), 0);
      medTableData.push([
        { content: pdf(t('report.catExpensesSubtotal')), colSpan: 4, styles: { fontStyle: 'bold', halign: 'right' } } as any,
        { content: `EUR ${medTotalCost.toFixed(2)}`, styles: { fontStyle: 'bold' } } as any,
        { content: '', colSpan: 2, styles: {} } as any,
      ]);

      autoTable(doc, {
        head: [[
          pdf(t('report.catId')),
          pdf(t('report.chipId')),
          pdf(t('common.date')),
          pdf(t('medical.eventType')),
          pdf(t('medical.costEuro')),
          pdf(t('medical.veterinary')),
          pdf(t('cat.notes')),
        ]],
        body: medTableData,
        startY: medStartY + 5,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] },
        columnStyles: { 4: { halign: 'right' } },
      });
    }

    // External cats expenses table
    if (externalCats().length > 0) {
      const pageHeight = doc.internal.pageSize.getHeight();
      const prevY = (doc as any).lastAutoTable?.finalY || 55;
      let exStartY: number;
      if (prevY + 30 > pageHeight - 20) { doc.addPage(); exStartY = 20; } else { exStartY = prevY + 15; }
      doc.setFontSize(13);
      doc.text(pdf(t('report.externalCatExpenses')), 14, exStartY);

      const exTableData: any[] = externalCats().map((cat) => [
        cat.cat_id.toString(),
        cat.chip_id?.toString() || t('cat.missing'),
        cat.cat_name || t('cat.unnamed'),
        cat.municipality || t('common.na'),
        `EUR ${cat.total_expenses.toFixed(2)}`,
      ]);
      exTableData.push([
        { content: pdf(t('report.catExpensesSubtotal')), colSpan: 4, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: `EUR ${externalTotal().toFixed(2)}`, styles: { fontStyle: 'bold' } },
      ]);

      autoTable(doc, {
        head: [[
          pdf(t('report.catId')),
          pdf(t('report.chipId')),
          pdf(t('report.name')),
          pdf(t('cat.municipality')),
          pdf(t('report.totalExpenses')),
        ]],
        body: exTableData,
        startY: exStartY + 5,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [237, 108, 2] },
        columnStyles: { 4: { halign: 'right' } },
      });
    }

    // External cats medical events table
    if (externalMedicalEvents().length > 0) {
      const pageHeight = doc.internal.pageSize.getHeight();
      const prevY = (doc as any).lastAutoTable?.finalY || 55;
      let emStartY: number;
      if (prevY + 30 > pageHeight - 20) { doc.addPage(); emStartY = 20; } else { emStartY = prevY + 15; }
      doc.setFontSize(13);
      doc.text(pdf(t('report.externalMedicalEventsTitle')), 14, emStartY);

      const extEvents = externalMedicalEvents();
      const emTableData: any[] = extEvents.map((ev) => [
        ev.cat_id.toString(),
        ev.chip_id || t('cat.missing'),
        ev.event_date ? dmyPdf(ev.event_date) : t('common.na'),
        pdf(translateEventType(ev.event_type)),
        ev.cost != null ? `EUR ${Number(ev.cost).toFixed(2)}` : t('common.na'),
        ev.veterinary_name || t('common.na'),
        ev.notes || '',
      ]);
      emTableData.push([
        { content: pdf(t('report.catExpensesSubtotal')), colSpan: 4, styles: { fontStyle: 'bold', halign: 'right' } } as any,
        { content: `EUR ${extEvents.reduce((s, ev) => s + (ev.cost || 0), 0).toFixed(2)}`, styles: { fontStyle: 'bold' } } as any,
        { content: '', colSpan: 2, styles: {} } as any,
      ]);

      autoTable(doc, {
        head: [[
          pdf(t('report.catId')),
          pdf(t('report.chipId')),
          pdf(t('common.date')),
          pdf(t('medical.eventType')),
          pdf(t('medical.costEuro')),
          pdf(t('medical.veterinary')),
          pdf(t('cat.notes')),
        ]],
        body: emTableData,
        startY: emStartY + 5,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] },
        columnStyles: { 4: { halign: 'right' } },
      });
    }

    // Municipality expenses table (cumulative external invoices)
    if (municipalityExpenses.length > 0) {
      const pageHeight = doc.internal.pageSize.getHeight();
      const prevY = (doc as any).lastAutoTable?.finalY || 55;
      let meStartY: number;
      if (prevY + 30 > pageHeight - 20) { doc.addPage(); meStartY = 20; } else { meStartY = prevY + 15; }
      doc.setFontSize(13);
      doc.text(pdf(t('report.municipalExpensesTitle')), 14, meStartY);

      const meTableData: any[] = municipalityExpenses.map((e) => [
        e.expense_date ? dmyPdf(e.expense_date) : t('common.na'),
        e.municipality || '',
        e.invoice_number || t('common.na'),
        e.description || '',
        e.notes || '',
        `EUR ${Number(e.amount || 0).toFixed(2)}`,
      ]);
      meTableData.push([
        { content: pdf(t('report.municipalExpensesSubtotal')), colSpan: 5, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: `EUR ${municipalExpensesTotal().toFixed(2)}`, styles: { fontStyle: 'bold' } },
      ]);

      autoTable(doc, {
        head: [[
          pdf(t('common.date')),
          pdf(t('cat.municipality')),
          pdf(t('expense.invoiceNumber')),
          pdf(t('expense.description')),
          pdf(t('cat.notes')),
          pdf(t('medical.costEuro')),
        ]],
        body: meTableData,
        startY: meStartY + 5,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [102, 126, 234] },
        columnStyles: { 5: { halign: 'right' } },
      });
    }

    // Sterilization recovery allowances table (external cats)
    if (recoveryAllowances.length > 0) {
      const pageHeight = doc.internal.pageSize.getHeight();
      const prevY = (doc as any).lastAutoTable?.finalY || 55;
      let rcStartY: number;
      if (prevY + 30 > pageHeight - 20) { doc.addPage(); rcStartY = 20; } else { rcStartY = prevY + 15; }
      doc.setFontSize(13);
      doc.text(pdf(t('report.recoveryTitle')), 14, rcStartY);

      const rcTableData: any[] = recoveryAllowances.map((r) => [
        r.cat_id.toString(),
        r.chip_id || t('cat.missing'),
        r.cat_name || t('cat.unnamed'),
        r.sex || t('common.na'),
        r.recovery_from ? dmyPdf(r.recovery_from) : t('common.na'),
        String(r.days),
        `EUR ${Number(r.daily_rate || 0).toFixed(2)}`,
        `EUR ${Number(r.amount || 0).toFixed(2)}`,
      ]);
      rcTableData.push([
        { content: pdf(t('report.catExpensesSubtotal')), colSpan: 7, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: `EUR ${recoveryTotal().toFixed(2)}`, styles: { fontStyle: 'bold' } },
      ]);

      autoTable(doc, {
        head: [[
          pdf(t('report.catId')),
          pdf(t('report.chipId')),
          pdf(t('report.name')),
          pdf(t('report.sex')),
          pdf(t('common.date')),
          pdf(t('report.recoveryDays')),
          pdf(t('report.dailyRate')),
          pdf(t('medical.costEuro')),
        ]],
        body: rcTableData,
        startY: rcStartY + 5,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [0, 137, 123] },
        columnStyles: { 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
      });
    }

    // Subtotals summary + grand total (after all detail tables)
    {
      const pageHeight = doc.internal.pageSize.getHeight();
      let sy = ((doc as any).lastAutoTable?.finalY || 55) + 12;
      if (sy + 50 > pageHeight - 20) { doc.addPage(); sy = 20; }
      doc.setFontSize(13);
      doc.text(pdf(t('report.summaryTitle')), 14, sy);
      doc.setFontSize(10);
      let ly = sy + 8;
      doc.text(`${pdf(t('report.subtotalShelter'))}: EUR ${shelterTotal().toFixed(2)}`, 14, ly);
      ly += 7;
      doc.text(`${pdf(t('report.subtotalExternal'))}: EUR ${externalTotal().toFixed(2)}`, 14, ly);
      if (recoveryAllowances.length > 0) {
        ly += 7;
        doc.text(`${pdf(t('report.subtotalRecovery'))}: EUR ${recoveryTotal().toFixed(2)}`, 14, ly);
      }
      ly += 7;
      doc.text(`${pdf(t('report.subtotalMunicipal'))}: EUR ${municipalExpensesTotal().toFixed(2)}`, 14, ly);
      doc.setFontSize(12);
      doc.text(`${pdf(t('report.grandTotal'))} EUR ${overallTotal().toFixed(2)}`, 14, ly + 10);
    }

    // Embed invoices
    const invoiceEvents = medicalEvents.filter((ev) => ev.invoice_file_path);
    if (invoiceEvents.length > 0) {
      // Collect image invoices to embed inline, PDF invoices to merge later
      const pdfInvoiceBuffers: ArrayBuffer[] = [];

      for (const ev of invoiceEvents) {
        const path = ev.invoice_file_path!;
        const ext = path.split('.').pop()?.toLowerCase();
        try {
          const resp = await api.get(`${API_URL}${path}`, { responseType: 'arraybuffer' });
          const buffer: ArrayBuffer = resp.data;
          if (ext === 'pdf') {
            pdfInvoiceBuffers.push(buffer);
          } else {
            // Image: embed on a new page
            doc.addPage();
            const blob = new Blob([buffer], { type: ext === 'png' ? 'image/png' : 'image/jpeg' });
            const dataUrl = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            });
            const filename = path.split('/').pop() || '';
            doc.setFontSize(10);
            doc.text(`${pdf(t('report.invoice'))}: ${filename}  |  Cat ID: ${ev.cat_id}  |  ${ev.event_date ? dmyPdf(ev.event_date) : ''}`, 14, 14);
            doc.addImage(dataUrl, ext === 'png' ? 'PNG' : 'JPEG', 10, 20, 277, 180);
          }
        } catch {
          // Skip invoices that can't be fetched
        }
      }

      // Merge PDF invoices using pdf-lib
      if (pdfInvoiceBuffers.length > 0) {
        try {
          const mainPdfBytes = doc.output('arraybuffer');
          const mergedPdf = await PDFDocument.load(mainPdfBytes);
          for (const buf of pdfInvoiceBuffers) {
            const invPdf = await PDFDocument.load(buf);
            const copiedPages = await mergedPdf.copyPages(invPdf, invPdf.getPageIndices());
            copiedPages.forEach((p) => mergedPdf.addPage(p));
          }
          const mergedBytes = await mergedPdf.save();
          const blob = new Blob([mergedBytes], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          const municipalityName2 = selectedMunicipality === 'All' ? 'All_Municipalities' : selectedMunicipality;
          a.href = url;
          a.download = `Expense_Report_${municipalityName2}_${startDate}_to_${endDate}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
          return;
        } catch {
          // Fall through to normal save if merge fails
        }
      }
    }

    // Save the PDF
    const municipalityName = selectedMunicipality === 'All' ? 'All_Municipalities' : selectedMunicipality;
    const fileName = `Expense_Report_${municipalityName}_${startDate}_to_${endDate}.pdf`;
    doc.save(fileName);
  };

  const handleExportExcel = async () => {
    const ORG = 'Gli Amici di Poldo';
    const municipalityLabel = selectedMunicipality === 'All' ? t('report.allMunicipalities') : selectedMunicipality;
    const dmy = (iso: string) => { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
    const generatedOn = () => {
      const d = new Date();
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    const workbook = new ExcelJS.Workbook();

    // --- Shared formatting helpers -------------------------------------
    const MONEY = '#,##0.00';
    // Long numeric ids (chips) must be written as text, otherwise Excel shows
    // them in scientific notation (1.23457E+11).
    const asText = (v: any) => (v === null || v === undefined || v === '' ? '' : String(v));
    const wrapCells = (row: any, cols: number[]) => {
      cols.forEach((c) => { row.getCell(c).alignment = { wrapText: true, vertical: 'top' }; });
    };
    // Grow the row so wrapped text is not clipped: estimate how many visual
    // lines each wrapping cell needs, given its column width.
    const fitHeight = (row: any, cols: { col: number; width: number }[]) => {
      let lines = 1;
      cols.forEach(({ col, width }) => {
        const text = String(row.getCell(col).value ?? '');
        const needed = text.split('\n').reduce(
          (acc, line) => acc + Math.max(1, Math.ceil(line.length / Math.max(8, width - 2))),
          0,
        );
        lines = Math.max(lines, needed);
      });
      row.height = Math.min(200, Math.max(20, lines * 16));
    };

    // Every sheet repeats who/where/when, so any printed page identifies itself.
    const addSheetHeader = (ws: any, sheetTitle: string) => {
      const org = ws.addRow([ORG]);
      org.font = { bold: true, size: 16, color: { argb: 'FF667EEA' } };
      org.height = 22;
      const title = ws.addRow([`${t('report.documentTitle')} — ${sheetTitle}`]);
      title.font = { bold: true, size: 13 };
      title.height = 20;
      [
        `${t('cat.municipality')}: ${municipalityLabel}`,
        `${t('report.dateRange')} ${dmy(startDate)} - ${dmy(endDate)}`,
        `${t('report.generatedOn')}: ${generatedOn()}`,
      ].forEach((text) => {
        const r = ws.addRow([text]);
        r.font = { size: 12 };
      });
      ws.addRow([]);
    };

    // freeze only for the first table of a sheet; a sheet can host a second
    // table (the expense detail) underneath.
    const addTableHeader = (ws: any, labels: string[], argb: string, freeze = true) => {
      const row = ws.addRow(labels);
      row.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
      row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      row.alignment = { wrapText: true, vertical: 'middle' };
      row.height = 36;
      if (freeze) ws.views = [{ state: 'frozen', ySplit: row.number }];
      return row;
    };

    const addSectionTitle = (ws: any, text: string) => {
      ws.addRow([]);
      const row = ws.addRow([text]);
      row.font = { bold: true, size: 12, color: { argb: 'FF1976D2' } };
      return row;
    };

    // Only the label cell is filled, so a long label overflows into the empty
    // cells instead of being clipped.
    const addTotalRow = (ws: any, label: string, totals: { col: number; value: number }[]) => {
      const row = ws.addRow([label]);
      totals.forEach(({ col, value }) => {
        row.getCell(col).value = value;
        row.getCell(col).numFmt = MONEY;
      });
      row.font = { bold: true, size: 12 };
      row.height = 20;
      return row;
    };

    // Recovery days belong to external cats: fold them in as a column.
    const recoveryByCat = new Map<number, number>();
    recoveryAllowances.forEach((r: any) => {
      recoveryByCat.set(r.cat_id, (recoveryByCat.get(r.cat_id) || 0) + Number(r.amount || 0));
    });
    const catNameById = new Map<number, string>();
    reportData.forEach((c) => catNameById.set(c.cat_id, c.cat_name || t('cat.unnamed')));

    const shelterList = shelterCats();
    const externalList = externalCats();

    // Sterilizations performed in the period. Those carried by a cumulative
    // invoice are excluded from the itemized detail (they hold no money of their
    // own), so they come in on their own list — otherwise this recap would
    // undercount against the statistics page.
    const sexByCat = new Map<number, string | null>();
    reportData.forEach((c) => sexByCat.set(c.cat_id, c.sex));
    invoiceSterilizations.forEach((s) => {
      if (!sexByCat.has(s.cat_id)) sexByCat.set(s.cat_id, s.sex);
    });
    const sterilizations = [
      ...[...shelterMedicalEvents(), ...externalMedicalEvents()]
        .filter((ev) => ev.event_type === 'Sterilization'),
      ...invoiceSterilizations,
    ];
    let stMale = 0, stFemale = 0, stUnknown = 0;
    sterilizations.forEach((ev) => {
      const s = sexByCat.get(ev.cat_id);
      if (s === 'M') stMale += 1;
      else if (s === 'F') stFemale += 1;
      else stUnknown += 1;
    });

    // ---------------- Sheet 1: Riepilogo -------------------------------
    const sumSheet = workbook.addWorksheet(t('report.sheetSummary'));
    sumSheet.columns = [{ width: 52 }, { width: 24 }];
    addSheetHeader(sumSheet, t('report.sheetSummary'));
    const catsShelterRow = sumSheet.addRow([`${t('report.sheetShelter')}: ${shelterList.length}`]);
    catsShelterRow.font = { size: 12 };
    const catsExternalRow = sumSheet.addRow([`${t('report.sheetExternal')}: ${externalList.length}`]);
    catsExternalRow.font = { size: 12 };
    sumSheet.addRow([]);

    const sumHeader = addTableHeader(sumSheet, [t('report.summaryItem'), t('report.amountEuro')], 'FF667EEA');
    sumHeader.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 13 };

    // Recovery days are part of the external cats' expenses, so they are not
    // listed as a separate line here.
    const summaryLines: { label: string; value: number }[] = [
      { label: t('report.subtotalShelter'), value: shelterTotal() },
      { label: t('report.subtotalExternal'), value: externalTotal() + recoveryTotal() },
      { label: t('report.subtotalMunicipal'), value: municipalExpensesTotal() },
    ];
    summaryLines.forEach(({ label, value }) => {
      const r = sumSheet.addRow([label]);
      r.getCell(2).value = value;
      r.getCell(2).numFmt = MONEY;
      r.font = { size: 13 };
      r.height = 20;
    });
    const grand = addTotalRow(sumSheet, t('report.totalToReimburse'), [{ col: 2, value: overallTotal() }]);
    grand.font = { bold: true, size: 15 };
    grand.height = 24;
    grand.eachCell({ includeEmpty: true }, (cell: any) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE0B2' } };
    });

    // Sterilizations recap
    sumSheet.addRow([]);
    const stHeader = addTableHeader(
      sumSheet,
      [t('statistics.sterilizationsTitle'), t('report.countLabel')],
      'FF00897B',
      false,
    );
    stHeader.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 13 };
    const stLines: { label: string; value: number }[] = [
      { label: t('statistics.male'), value: stMale },
      { label: t('statistics.female'), value: stFemale },
    ];
    if (stUnknown > 0) stLines.push({ label: t('statistics.unknown'), value: stUnknown });
    stLines.forEach(({ label, value }) => {
      const r = sumSheet.addRow([label]);
      r.getCell(2).value = value;
      r.font = { size: 13 };
      r.height = 20;
    });
    const stTotalRow = sumSheet.addRow([t('statistics.total')]);
    stTotalRow.getCell(2).value = sterilizations.length;
    stTotalRow.font = { bold: true, size: 14 };
    stTotalRow.height = 22;

    // ---------------- Sheet 2: Gatti Rifugio ---------------------------
    if (shelterList.length > 0) {
      const ws = workbook.addWorksheet(t('report.sheetShelter').substring(0, 31));
      const w = [8, 20, 20, 26, 12, 26, 58, 18, 18, 16];
      ws.columns = w.map((width) => ({ width }));
      addSheetHeader(ws, t('report.sheetShelter'));
      addTableHeader(ws, [
        t('report.catId'), t('report.chipId'), t('report.name'), t('report.status'), t('report.sex'),
        t('report.addressWhereFound'), t('report.costClass'),
        t('report.totalMedicalExpenses'), t('report.totalDailyAllowance'), t('report.totalExpenses'),
      ], 'FFED6C02');

      shelterList.forEach((cat) => {
        const costClassText = formatCostClassCell(cat.cost_class_periods, '\n', dmy);
        const statusText = cat.status_date
          ? `${translateStatus(cat.status, cat.external)} (${dmy(cat.status_date.toString().slice(0, 10))})`
          : translateStatus(cat.status, cat.external);
        const row = ws.addRow([
          cat.cat_id,
          asText(cat.chip_id || t('cat.missing')),
          cat.cat_name || t('cat.unnamed'),
          statusText,
          cat.sex || t('common.na'),
          cat.address_where_found || t('common.na'),
          costClassText,
          cat.total_medical_expenses,
          cat.total_daily_allowance,
          cat.total_expenses,
        ]);
        row.font = { size: 12 };
        row.getCell(2).numFmt = '@';
        [8, 9, 10].forEach((c) => { row.getCell(c).numFmt = MONEY; });
        wrapCells(row, [4, 6, 7]);
        fitHeight(row, [{ col: 4, width: w[3] }, { col: 6, width: w[5] }, { col: 7, width: w[6] }]);
      });

      addTotalRow(ws, t('report.catExpensesSubtotal'), [
        { col: 8, value: calculateTotalMedicalExpenses(shelterList) },
        { col: 9, value: calculateTotalDailyAllowance(shelterList) },
        { col: 10, value: shelterTotal() },
      ]);

      // Itemised expenses right under the cats they belong to.
      const shelterEvents = shelterMedicalEvents();
      if (shelterEvents.length > 0) {
        addSectionTitle(ws, t('report.shelterMedicalEventsTitle'));
        addTableHeader(ws, [
          t('report.catId'), t('report.chipId'), t('report.name'),
          t('medical.eventType'), t('common.date'), t('medical.veterinary'),
          t('cat.notes'), t('medical.costEuro'),
        ], 'FF1976D2', false);

        let detailTotal = 0;
        shelterEvents.forEach((ev) => {
          const cost = ev.cost != null ? Number(ev.cost) : null;
          if (cost) detailTotal += cost;
          const row = ws.addRow([
            ev.cat_id,
            asText(ev.chip_id || t('cat.missing')),
            catNameById.get(ev.cat_id) || t('cat.unnamed'),
            translateEventType(ev.event_type),
            ev.event_date ? dmy(ev.event_date) : t('common.na'),
            ev.veterinary_name || t('common.na'),
            ev.notes || '',
            cost,
          ]);
          row.font = { size: 12 };
          row.getCell(2).numFmt = '@';
          if (cost != null) row.getCell(8).numFmt = MONEY;
          wrapCells(row, [6, 7]);
          fitHeight(row, [{ col: 6, width: w[5] }, { col: 7, width: w[6] }]);
        });
        addTotalRow(ws, t('report.catExpensesSubtotal'), [{ col: 8, value: detailTotal }]);
      }
    }

    // ---------------- Sheet 3: Gatti Esterni ---------------------------
    if (externalList.length > 0) {
      const ws = workbook.addWorksheet(t('report.sheetExternal').substring(0, 31));
      const w = [8, 20, 22, 12, 24, 22, 28, 18];
      ws.columns = w.map((width) => ({ width }));
      addSheetHeader(ws, t('report.sheetExternal'));
      addTableHeader(ws, [
        t('report.catId'), t('report.chipId'), t('report.name'), t('report.sex'), t('cat.municipality'),
        t('report.totalMedicalExpenses'), t('report.recoveryAllowanceCol'), t('report.totalExpenses'),
      ], 'FFED6C02');

      let medSum = 0;
      let recSum = 0;
      externalList.forEach((cat) => {
        const med = Number(cat.total_medical_expenses || 0);
        const rec = recoveryByCat.get(cat.cat_id) || 0;
        medSum += med;
        recSum += rec;
        const row = ws.addRow([
          cat.cat_id,
          asText(cat.chip_id || t('cat.missing')),
          cat.cat_name || t('cat.unnamed'),
          cat.sex || t('common.na'),
          cat.municipality || t('common.na'),
          med,
          rec,
          med + rec,
        ]);
        row.font = { size: 12 };
        row.getCell(2).numFmt = '@';
        [6, 7, 8].forEach((c) => { row.getCell(c).numFmt = MONEY; });
      });

      addTotalRow(ws, t('report.catExpensesSubtotal'), [
        { col: 6, value: medSum },
        { col: 7, value: recSum },
        { col: 8, value: medSum + recSum },
      ]);

      // Itemised expenses right under the cats they belong to.
      const externalEvents = externalMedicalEvents();
      if (externalEvents.length > 0) {
        addSectionTitle(ws, t('report.externalMedicalEventsTitle'));
        addTableHeader(ws, [
          t('report.catId'), t('report.chipId'), t('report.name'),
          t('common.date'), t('medical.eventType'), t('medical.veterinary'),
          t('cat.notes'), t('medical.costEuro'),
        ], 'FF1976D2', false);

        let detailTotal = 0;
        externalEvents.forEach((ev) => {
          const cost = ev.cost != null ? Number(ev.cost) : null;
          if (cost) detailTotal += cost;
          const row = ws.addRow([
            ev.cat_id,
            asText(ev.chip_id || t('cat.missing')),
            catNameById.get(ev.cat_id) || t('cat.unnamed'),
            ev.event_date ? dmy(ev.event_date) : t('common.na'),
            translateEventType(ev.event_type),
            ev.veterinary_name || t('common.na'),
            ev.notes || '',
            cost,
          ]);
          row.font = { size: 12 };
          row.getCell(2).numFmt = '@';
          if (cost != null) row.getCell(8).numFmt = MONEY;
          wrapCells(row, [6, 7]);
          fitHeight(row, [{ col: 6, width: w[5] }, { col: 7, width: w[6] }]);
        });
        addTotalRow(ws, t('report.catExpensesSubtotal'), [{ col: 8, value: detailTotal }]);
      }
    }

    // ------- Sheet 4: Spese Generiche o per Gatti Multipli --------------
    if (municipalityExpenses.length > 0) {
      const ws = workbook.addWorksheet(t('report.sheetInvoices').substring(0, 31));
      const w = [14, 24, 16, 42, 42, 18];
      ws.columns = w.map((width) => ({ width }));
      addSheetHeader(ws, t('report.municipalExpensesTitle'));
      addTableHeader(ws, [
        t('common.date'), t('cat.municipality'), t('expense.invoiceNumber'),
        t('expense.description'), t('cat.notes'), t('medical.costEuro'),
      ], 'FF667EEA');

      municipalityExpenses.forEach((e) => {
        const row = ws.addRow([
          e.expense_date ? dmy(e.expense_date) : t('common.na'),
          e.municipality || '',
          asText(e.invoice_number || t('common.na')),
          e.description || '',
          e.notes || '',
          Number(e.amount || 0),
        ]);
        row.font = { size: 12 };
        row.getCell(3).numFmt = '@';
        row.getCell(6).numFmt = MONEY;
        wrapCells(row, [4, 5]);
        fitHeight(row, [{ col: 4, width: w[3] }, { col: 5, width: w[4] }]);
      });

      addTotalRow(ws, t('report.municipalExpensesSubtotal'), [
        { col: 6, value: municipalExpensesTotal() },
      ]);
    }

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const municipalityName = selectedMunicipality === 'All' ? 'All_Municipalities' : selectedMunicipality;
    a.download = `Expense_Report_${municipalityName}_${startDate}_to_${endDate}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          background: 'white',
          boxShadow: '0 4px 16px rgba(102,126,234,0.12)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
              boxShadow: '0 3px 8px rgba(250,112,154,0.35)'
            }}
          >
            <ReportIcon sx={{ fontSize: 24, color: 'white' }} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#667eea' }}>
            {t('report.title')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('report.description')}
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {/* Report Form */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            {t('report.parameters')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <DateField
              label={t('report.startDate')}
              value={startDate}
              onChange={(v) => setStartDate(v)}
              required
              sx={{ flex: 1, minWidth: 200 }}
            />

            <DateField
              label={t('report.endDate')}
              value={endDate}
              onChange={(v) => setEndDate(v)}
              required
              sx={{ flex: 1, minWidth: 200 }}
            />

            <FormControl sx={{ flex: 1, minWidth: 200 }} required>
              <InputLabel>{t('cat.municipality')}</InputLabel>
              <Select
                value={selectedMunicipality}
                label={t('cat.municipality')}
                onChange={(e) => setSelectedMunicipality(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('report.selectMunicipality')}</em>
                </MenuItem>
                <MenuItem value="All">
                  <strong>{t('report.allMunicipalities')}</strong>
                </MenuItem>
                {municipalities.map((municipality) => (
                  <MenuItem key={municipality} value={municipality}>
                    {municipality}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleGenerateReport}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <ReportIcon />}
            fullWidth
            sx={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(250,112,154,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e95f89 0%, #edd02f 100%)',
                boxShadow: '0 6px 16px rgba(250,112,154,0.5)',
              },
              '&:disabled': {
                background: '#ccc',
                color: '#666',
              }
            }}
          >
            {loading ? t('report.generatingReport') : t('report.generateReport')}
          </Button>
        </Box>

        {/* Report Results */}
        {(reportData.length > 0 || municipalityExpenses.length > 0 || recoveryAllowances.length > 0) && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('report.reportResults')} {selectedMunicipality === 'All' ? '' : `${t('cat.municipality')}: `}<strong style={{ color: '#ed6c02' }}>{selectedMunicipality === 'All' ? t('report.allMunicipalities') : selectedMunicipality}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('report.dateRange')} <strong>{startDate}</strong> {t('common.to')} <strong>{endDate}</strong> |
                  {t('report.totalCats')} <strong>{reportData.length}</strong>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<PdfIcon />}
                  onClick={() => handleExportPDF()}
                  sx={{
                    height: 'fit-content',
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(240,147,251,0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)',
                      boxShadow: '0 6px 16px rgba(240,147,251,0.5)',
                    }
                  }}
                >
                  {t('report.exportPdf')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ExcelIcon />}
                  onClick={handleExportExcel}
                  sx={{
                    height: 'fit-content',
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    color: 'white',
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(67,233,123,0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #38d46e 0%, #2de0bf 100%)',
                      boxShadow: '0 6px 16px rgba(67,233,123,0.5)',
                    }
                  }}
                >
                  {t('report.exportExcel')}
                </Button>
              </Box>
            </Box>

            {/* Shelter Cat Expenses Section */}
            {shelterCats().length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ color: '#ed6c02', fontWeight: 600 }}>
                  {t('report.shelterCatExpenses')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'warning.light' }}>
                        <TableCell><strong>{t('report.catId')}</strong></TableCell>
                        <TableCell><strong>{t('report.chipId')}</strong></TableCell>
                        <TableCell><strong>{t('report.name')}</strong></TableCell>
                        <TableCell><strong>{t('report.status')}</strong></TableCell>
                        <TableCell><strong>{t('report.sex')}</strong></TableCell>
                        <TableCell><strong>{t('report.addressWhereFound')}</strong></TableCell>
                        <TableCell><strong>{t('report.costClass')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('report.totalMedicalExpensesEuro')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('report.totalDailyAllowanceEuro')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('report.totalExpensesEuro')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shelterCats().map((cat) => (
                    <TableRow key={cat.cat_id} hover>
                      <TableCell>{cat.cat_id}</TableCell>
                      <TableCell>{cat.chip_id || t('cat.missing')}</TableCell>
                      <TableCell>{cat.cat_name || t('cat.unnamed')}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: cat.external ? 'warning.main' : cat.status?.toLowerCase() === 'active' ? 'success.main' : cat.status?.toLowerCase() === 'angel' ? 'error.main' : 'info.main',
                              fontWeight: 600
                            }}
                          >
                            {translateStatus(cat.status, cat.external)}
                          </Typography>
                          {cat.status_date && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(cat.status_date.toString().slice(0, 10))}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{cat.sex || t('common.na')}</TableCell>
                      <TableCell>{cat.address_where_found || t('common.na')}</TableCell>
                      <TableCell>
                        {formatCostClassCell(cat.cost_class_periods, '\n', formatDate)
                          .split('\n').map((line, i) => (
                            <Typography key={i} variant="caption" display="block" sx={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
                              {line}
                            </Typography>
                          ))}
                      </TableCell>
                      <TableCell align="right">
                        €{cat.total_medical_expenses.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        €{cat.total_daily_allowance.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <strong>€{cat.total_expenses.toFixed(2)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'warning.light' }}>
                    <TableCell colSpan={7} align="right">
                      <strong>{t('report.catExpensesSubtotal')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>€{calculateTotalMedicalExpenses(shelterCats()).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>€{calculateTotalDailyAllowance(shelterCats()).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>€{shelterTotal().toFixed(2)}</strong>
                    </TableCell>
                  </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Shelter Cats Medical Events Detail */}
            {shelterMedicalEvents().length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600, mt: 3 }}>
                  {t('report.shelterMedicalEventsTitle')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell><strong>{t('report.catId')}</strong></TableCell>
                        <TableCell><strong>{t('report.chipId')}</strong></TableCell>
                        <TableCell><strong>{t('common.date')}</strong></TableCell>
                        <TableCell><strong>{t('medical.eventType')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('medical.costEuro')}</strong></TableCell>
                        <TableCell><strong>{t('medical.veterinary')}</strong></TableCell>
                        <TableCell><strong>{t('cat.notes')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shelterMedicalEvents().map((ev, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{ev.cat_id}</TableCell>
                          <TableCell>{ev.chip_id || t('cat.missing')}</TableCell>
                          <TableCell>{ev.event_date ? formatDate(ev.event_date) : t('common.na')}</TableCell>
                          <TableCell>{translateEventType(ev.event_type)}</TableCell>
                          <TableCell align="right">{ev.cost != null ? `€${Number(ev.cost).toFixed(2)}` : t('common.na')}</TableCell>
                          <TableCell>{ev.veterinary_name || t('common.na')}</TableCell>
                          <TableCell sx={{ maxWidth: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ev.notes || ''}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell colSpan={4} align="right">
                          <strong>{t('report.catExpensesSubtotal')}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>€{shelterMedicalEvents().reduce((s, ev) => s + (ev.cost || 0), 0).toFixed(2)}</strong>
                        </TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* External Cat Expenses Section */}
            {externalCats().length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ color: '#ed6c02', fontWeight: 600, mt: 3 }}>
                  {t('report.externalCatExpenses')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'warning.light' }}>
                        <TableCell><strong>{t('report.catId')}</strong></TableCell>
                        <TableCell><strong>{t('report.chipId')}</strong></TableCell>
                        <TableCell><strong>{t('report.name')}</strong></TableCell>
                        <TableCell><strong>{t('cat.municipality')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('report.totalExpensesEuro')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {externalCats().map((cat) => (
                        <TableRow key={cat.cat_id} hover>
                          <TableCell>{cat.cat_id}</TableCell>
                          <TableCell>{cat.chip_id || t('cat.missing')}</TableCell>
                          <TableCell>{cat.cat_name || t('cat.unnamed')}</TableCell>
                          <TableCell>{cat.municipality || t('common.na')}</TableCell>
                          <TableCell align="right"><strong>€{cat.total_expenses.toFixed(2)}</strong></TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'warning.light' }}>
                        <TableCell colSpan={4} align="right"><strong>{t('report.catExpensesSubtotal')}</strong></TableCell>
                        <TableCell align="right"><strong>€{externalTotal().toFixed(2)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* External Cats Medical Events Detail */}
            {externalMedicalEvents().length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600, mt: 3 }}>
                  {t('report.externalMedicalEventsTitle')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell><strong>{t('report.catId')}</strong></TableCell>
                        <TableCell><strong>{t('report.chipId')}</strong></TableCell>
                        <TableCell><strong>{t('common.date')}</strong></TableCell>
                        <TableCell><strong>{t('medical.eventType')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('medical.costEuro')}</strong></TableCell>
                        <TableCell><strong>{t('medical.veterinary')}</strong></TableCell>
                        <TableCell><strong>{t('cat.notes')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {externalMedicalEvents().map((ev, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{ev.cat_id}</TableCell>
                          <TableCell>{ev.chip_id || t('cat.missing')}</TableCell>
                          <TableCell>{ev.event_date ? formatDate(ev.event_date) : t('common.na')}</TableCell>
                          <TableCell>{translateEventType(ev.event_type)}</TableCell>
                          <TableCell align="right">{ev.cost != null ? `€${Number(ev.cost).toFixed(2)}` : t('common.na')}</TableCell>
                          <TableCell>{ev.veterinary_name || t('common.na')}</TableCell>
                          <TableCell sx={{ maxWidth: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ev.notes || ''}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell colSpan={4} align="right"><strong>{t('report.catExpensesSubtotal')}</strong></TableCell>
                        <TableCell align="right"><strong>€{externalMedicalEvents().reduce((s, ev) => s + (ev.cost || 0), 0).toFixed(2)}</strong></TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Municipality Expenses (cumulative external invoices) */}
            {municipalityExpenses.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ color: '#667eea', fontWeight: 600, mt: 3 }}>
                  {t('report.municipalExpensesTitle')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell><strong>{t('common.date')}</strong></TableCell>
                        <TableCell><strong>{t('cat.municipality')}</strong></TableCell>
                        <TableCell><strong>{t('expense.invoiceNumber')}</strong></TableCell>
                        <TableCell><strong>{t('expense.description')}</strong></TableCell>
                        <TableCell><strong>{t('cat.notes')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('medical.costEuro')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {municipalityExpenses.map((e, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{e.expense_date ? formatDate(e.expense_date) : t('common.na')}</TableCell>
                          <TableCell>{e.municipality}</TableCell>
                          <TableCell>{e.invoice_number || t('common.na')}</TableCell>
                          <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.description || ''}</TableCell>
                          <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.notes || ''}</TableCell>
                          <TableCell align="right">€{Number(e.amount || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell colSpan={5} align="right"><strong>{t('report.municipalExpensesSubtotal')}</strong></TableCell>
                        <TableCell align="right"><strong>€{municipalExpensesTotal().toFixed(2)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Sterilization recovery allowances (external cats) */}
            {recoveryAllowances.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ color: '#00897b', fontWeight: 600, mt: 3 }}>
                  {t('report.recoveryTitle')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell><strong>{t('report.catId')}</strong></TableCell>
                        <TableCell><strong>{t('report.chipId')}</strong></TableCell>
                        <TableCell><strong>{t('report.name')}</strong></TableCell>
                        <TableCell><strong>{t('report.sex')}</strong></TableCell>
                        <TableCell><strong>{t('common.date')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('report.recoveryDays')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('report.dailyRate')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('medical.costEuro')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recoveryAllowances.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{r.cat_id}</TableCell>
                          <TableCell>{r.chip_id || t('cat.missing')}</TableCell>
                          <TableCell>{r.cat_name || t('cat.unnamed')}</TableCell>
                          <TableCell>{r.sex || t('common.na')}</TableCell>
                          <TableCell>{r.recovery_from ? formatDate(r.recovery_from) : t('common.na')}</TableCell>
                          <TableCell align="right">{r.days}</TableCell>
                          <TableCell align="right">€{Number(r.daily_rate || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">€{Number(r.amount || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell colSpan={7} align="right"><strong>{t('report.catExpensesSubtotal')}</strong></TableCell>
                        <TableCell align="right"><strong>€{recoveryTotal().toFixed(2)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Subtotals summary + grand total */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{t('report.summaryTitle')}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 480, ml: 'auto' }}>
                <Typography variant="body2" color="text.secondary">{t('report.subtotalShelter')}</Typography>
                <Typography variant="body2">€{shelterTotal().toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 480, ml: 'auto' }}>
                <Typography variant="body2" color="text.secondary">{t('report.subtotalExternal')}</Typography>
                <Typography variant="body2">€{externalTotal().toFixed(2)}</Typography>
              </Box>
              {recoveryAllowances.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 480, ml: 'auto' }}>
                  <Typography variant="body2" color="text.secondary">{t('report.subtotalRecovery')}</Typography>
                  <Typography variant="body2">€{recoveryTotal().toFixed(2)}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 480, ml: 'auto' }}>
                <Typography variant="body2" color="text.secondary">{t('report.subtotalMunicipal')}</Typography>
                <Typography variant="body2">€{municipalExpensesTotal().toFixed(2)}</Typography>
              </Box>
              <Box sx={{ borderTop: '1px solid #ddd', mt: 1, pt: 1 }}>
                <Typography variant="h5" align="right" sx={{ fontWeight: 700, color: '#333' }}>
                  {t('report.grandTotal')} €{overallTotal().toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Report;
